import React from "react";
import { Link } from "react-router-dom";

interface ReportLink {
  to: string;
  name: string;
  oldEquivalent: string;
  description: string;
}

const REPORTS: ReportLink[] = [
  {
    to: "/reports-new/financial",
    name: "Financial (group by …)",
    oldEquivalent: "Financial / Sales reports, unified — incl. Vendor Sales & Vendor Type Sales",
    description:
      "One consolidated financial report — group by Event, Vendor, Location, Vendor Type, Day, or Clerk. This is the replacement for the old VENDOR SALES report (group by = Vendor) and VENDOR TYPE SALES report (group by = Vendor Type): the full legacy column set — per-tender gross sales and transaction counts, net deductions, per-tender tips, service fee, refunds, Merchant Processed — in the legacy order. Net Sales, Gross, Tax, Tips, Service Fee, Discounts, Refunds, Orders, Transactions on every grouping. (Show/Sub-Event coming.)",
  },
  // Deep links into the SAME Financial page with the group-by preset (RP-2155/2159). They exist so a
  // QA hunting for "Vendor Sales" lands on the report that actually replaces it — the retired
  // 2-column /reports-new/sales-by-vendor stub used to catch them here and bounce the ticket.
  {
    to: "/reports-new/financial?dimension=Vendor",
    name: "Vendor Sales",
    oldEquivalent: "Vendor Sales Report",
    description:
      "The Financial report opened with Group by = Vendor — the legacy Vendor Sales column set in legacy order. One row per vendor.",
  },
  {
    to: "/reports-new/financial?dimension=VendorType",
    name: "Vendor Type Sales",
    oldEquivalent: "Vendor Type Sales Report",
    description:
      "The Financial report opened with Group by = Vendor Type — same columns as Vendor Sales, rolled up per vendor type.",
  },
  {
    to: "/reports-new/ticketing-financial",
    name: "Ticketing Financial",
    oldEquivalent: "Financial Report (Ticketing)",
    description:
      "The legacy 11-column ticketing financial report, restricted to ticketing orders — canonical Gross/Net plus Discounts vs Promo Codes, pre-tax & gross refunds, per-tender tips, and Merchant Processed. Default rows by Date; re-pivot by Event/Vendor/Location/Clerk.",
  },
  {
    to: "/reports-new/net-hourly",
    name: "Net Hourly",
    oldEquivalent: "Net Hourly / Cumulative Hourly",
    description:
      "Net Sales by hour as a line graph — toggle cumulative, overlay previous period / previous year. Filter by event, vendor, location.",
  },
  {
    to: "/reports-new/ticketing-net-hourly",
    name: "Ticketing Net Hourly",
    oldEquivalent: "Ticketing Net Hourly Sales Report",
    description:
      "The legacy ticketing Net Hourly report, restricted to ticketing orders — Net Sales by hour (days as rows, 5 AM operating-day hours as columns), canonical real-money Net. Toggle cumulative; filter by event, vendor, location.",
  },
  {
    to: "/reports-new/vertical-sales",
    name: "Vertical Sales",
    oldEquivalent: "Vertical Sales Report",
    description:
      "Event-level stacked view: financial summary, payment mix, card-brand breakdown, and product mix in one page.",
  },
  {
    to: "/reports-new/discounts",
    name: "Discounts & Promo Codes",
    oldEquivalent: "Discounts / Promo Codes reports",
    description:
      "Per whole-order discount/promo: times used + dollar total, split into customer promo codes vs admin discounts.",
  },
  {
    to: "/reports-new/event-summary",
    name: "Event Summary",
    oldEquivalent: "Event Summary (dashboard)",
    description:
      "One event: full financial set + Net-by-hour graph + top products. (Marc's planned login splash / widgets — follow-on.)",
  },
  {
    to: "/reports-new/transactions",
    name: "Transactions",
    oldEquivalent: "Transactions Report",
    description:
      "Transaction detail — one row per transaction, scoped + exportable. Approved txns feed the count/amount totals.",
  },
  {
    to: "/reports-new/order-items",
    name: "Order Items",
    oldEquivalent: "Order Items Report",
    description:
      "Order-item detail for inventory / balancing — line subtotal/tax/discount + applied modifiers (sparse until backfill). Exportable.",
  },
  {
    to: "/reports-new/tab-management",
    name: "Tab Management",
    oldEquivalent: "Tab Management Report",
    description:
      "One row per POS tab order — open, in-use, deleted, or closed. Username, tab name/nickname, last 4 of card, raw amounts + canonical discount, and items. Scope by event/date/vendor/location; open tabs included. Exportable.",
  },
  {
    to: "/reports-new/web-checkout",
    name: "Web Checkout",
    oldEquivalent: "Web Checkout Report",
    description:
      "One row per legacy web-checkout order — transaction date, processor reference, selection, client + billing details, card, and raw amounts (Subtotal/Service Fee/Total). Scope by event/date; exportable. Note: legacy Reference ID was not migrated.",
  },
  {
    to: "/reports-new/commissary",
    name: "Commissary",
    oldEquivalent: "Commissary Report",
    description:
      "Commissary orders grouped by vendor — expandable orders with line items (Item / Qty / Unit Price / Line Total / Notes), status, placed-by, and notes. Vendor totals sum FULFILLED orders only (like the old report); manual payout adjustments are excluded. Commissary never appears in the sales reports — this page is its home.",
  },
  {
    to: "/reports-new/financial-summary",
    name: "Financial Summary by Event",
    oldEquivalent: "Financial Report / Event Summary",
    description:
      "Net Sales, Gross, Tax, Discounts, Refunds, Tips, Service Fee per event — corrected definitions.",
  },
  {
    to: "/reports-new/financial-by-day",
    name: "Financial Summary by Day",
    oldEquivalent: "Financial Report (daily rows)",
    description:
      "One row per business day — Gross, Net, Tax, Tips, Service Fee, Discounts, Refunds, Orders, Transactions. Pick a single event to compare against the old daily export.",
  },
  {
    to: "/reports-new/sales-by-day",
    name: "Sales by Day",
    oldEquivalent: "Net Hourly / financial date trend",
    description: "Daily Gross + order count time series on the engine's business-day boundary.",
  },
  {
    to: "/reports-new/product-mix",
    name: "Product Mix",
    oldEquivalent: "Product Mix Report",
    description: "Units, Item Revenue, Item Tax, Item Discount — by item or by category.",
  },
  {
    to: "/reports-new/transaction-audit",
    name: "Transaction Audit (Reconciliation)",
    oldEquivalent: "Transactions Report (+ reconciliation)",
    description:
      "Every transaction/order-item with its day, tender, inclusion reason, and Gross/Net contribution. Totals reconcile to the summary pages — sort/filter straight to the rows behind any mismatch.",
  },
  {
    to: "/reports-new/reconciliation",
    name: "Reconciliation (Old vs New)",
    oldEquivalent: "the reconciliation doc",
    description:
      "Pick an event → old-vs-new totals, the itemized gross gap, the Gross→Net bridge, and the extra transactions. Explains why New Gross runs higher while Net matches.",
  },
];

// Landing page for the parallel new-reporting pages. Purpose: QA/ops open the matching
// OLD report in one tab and the NEW one here to compare numbers side by side.
export default function ReportsNewLanding() {
  return (
    <div className="px-1 py-4">
      <div className="mb-4 flex items-center gap-3">
        <span className="inline-flex items-center rounded-full bg-primary-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
          New Reporting Engine
        </span>
        <h2 className="text-2xl font-bold">New Reporting (Parallel — Comparison)</h2>
      </div>

      <p className="mb-4 max-w-3xl text-sm text-gray-600">
        These pages pull from the NEW unified reporting engine (<code>/reports/query</code>) with
        the corrected metric definitions. They run alongside the existing reports — open the old
        report and its new counterpart side by side to spot-check the numbers. The old reports are
        unchanged.
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {REPORTS.map((r) => (
          <Link
            key={r.to}
            to={r.to}
            className="block rounded-md border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="text-lg font-semibold text-gray-900">{r.name}</div>
            <div className="mt-1 text-xs font-medium uppercase tracking-wide text-primary-700">
              old: {r.oldEquivalent}
            </div>
            <p className="mt-2 text-sm text-gray-600">{r.description}</p>
          </Link>
        ))}
      </div>

      <div className="mt-6 max-w-3xl rounded-md border border-gray-200 bg-gray-50 p-4 text-xs text-gray-500">
        <div className="font-semibold text-gray-700">How this is wired</div>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>
            Requests go through a same-origin proxy that holds the engine credential server-side —
            deployed: <code>/api/reporting</code> (Vercel function, Ronin-staff only); dev:{" "}
            <code>/new-reporting-api</code> → <code>http://localhost:5382</code> (override with{" "}
            <code>VITE_NEW_REPORTING_TARGET</code>). No CORS changes on the API, no engine
            credentials in the browser.
          </li>
          <li>
            For local dev, start the new API first: <code>dotnet run</code> in{" "}
            <code>Distributed-Systems/Ronin.ApiService</code> (dev auth is injected server-side
            by the Vite plugin — see <code>reports-new/README.md</code>).
          </li>
        </ul>
      </div>
    </div>
  );
}
